/**
 * CrisisPractitionerPrompt Component
 *
 * User-initiated crisis support request flow. Displayed after crisis detection
 * if the patient has practitioners with crisis notification consent.
 *
 * Flow:
 * 1. PROMPT_STATE: Ask if user wants to reach out
 * 2. SELECTOR_STATE: Show practitioner selector
 * 3. SENDING_STATE: Show loading while sending
 * 4. SUCCESS_STATE: Confirmation with reminder
 * 5. ERROR_STATE: Error with retry/contact options
 *
 * Security:
 * - Requires JWT authentication
 * - Only practitioners with allow_crisis_notifications=true shown
 * - share_original_message defaults to false (GDPR compliant)
 * - 72-hour expiration on all requests
 *
 * Design Principles (Crisis UX Best Practices):
 * - NON-COERCIVE: User actively chooses to request support
 * - ACTIONABLE: Clear CTAs with minimal steps
 * - PRIVACY-FIRST: Message content not shared by default
 * - ACCESSIBLE: 56px touch targets, WCAG AAA compliant
 *
 * Created: 2025-12-18
 * Phase: 2.5 - Crisis Safeguards Integration
 */

import { useState, useCallback, useRef } from 'react';
import {
  View,
  Modal,
  ScrollView,
} from 'react-native';
import { useTranslation } from '@dramadetector/shared';
import {
  useCrisisAssistance,
  usePractitionerCrisisConsent,
  type PractitionerCrisisConsent,
  type CrisisUrgencyLevel,
} from '@dramadetector/api-client';
import { useAuth } from '../../contexts/AuthContext';
import { captureEvent } from '../../lib/analytics';
import { SAFETY_PRACTITIONER_EVENTS } from '../../lib/analytics/events/safety';
import { styles } from './styles/crisis-practitioner.styles';
import { SendingContent, SuccessContent, ErrorContent } from './components/CrisisStatusViews';
import { SelectorContent } from './components/CrisisSelectorContent';
import { PromptContent } from './components/CrisisPromptContent';

/**
 * Component state machine states
 */
type PromptState =
  | 'prompt' // Initial prompt asking if user wants to reach out
  | 'selector' // Practitioner selection
  | 'sending' // Sending request
  | 'success' // Request sent successfully
  | 'error'; // Error occurred

export interface CrisisPractitionerPromptProps {
  /**
   * Whether the prompt is visible
   */
  visible: boolean;

  /**
   * Moderation event ID that triggered crisis detection
   * Required to link the request to the triggering event
   */
  moderationEventId?: string;

  /**
   * Crisis severity level for urgency classification
   */
  severity?: 'low' | 'medium' | 'high' | 'critical';

  /**
   * Callback when prompt is closed
   */
  onClose: () => void;

  /**
   * Callback when request is sent successfully
   */
  onRequestSent?: (practitionerId: string) => void;
}

/**
 * Map severity to urgency level for the API
 */
function severityToUrgency(severity?: string): CrisisUrgencyLevel {
  switch (severity) {
    case 'critical':
      return 'critical'; // Matches database CHECK constraint
    case 'high':
      return 'urgent';
    default:
      return 'standard';
  }
}

/**
 * CrisisPractitionerPrompt - User-initiated practitioner support request
 */
export function CrisisPractitionerPrompt({
  visible,
  moderationEventId,
  severity = 'medium',
  onClose,
  onRequestSent,
}: CrisisPractitionerPromptProps) {
  const { t: tFn } = useTranslation('safety');
  const t = tFn as (key: string, options?: Record<string, unknown>) => string;
  const { user } = useAuth();

  // Component state
  const [promptState, setPromptState] = useState<PromptState>('prompt');
  const [selectedPractitioner, setSelectedPractitioner] =
    useState<PractitionerCrisisConsent | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // P1-6: Guard against duplicate request submissions
  // Prevents race condition where rapid taps could trigger multiple API calls
  const isRequestInFlightRef = useRef(false);

  // Fetch practitioners with crisis consent
  const { data: practitioners, isLoading: loadingPractitioners } =
    usePractitionerCrisisConsent(user?.id, visible);

  // Crisis assistance mutation
  const { mutateAsync: sendRequest } =
    useCrisisAssistance(user?.id);

  // Handle "Request Support" button click (from prompt state)
  const handleRequestSupport = useCallback(() => {
    captureEvent(SAFETY_PRACTITIONER_EVENTS.REQUEST_SUPPORT_TAPPED, {
      severity,
      practitioner_count: practitioners?.length || 0,
    });

    // If only one practitioner, skip selector
    if (practitioners && practitioners.length === 1) {
      setSelectedPractitioner(practitioners[0]);
      setPromptState('selector');
    } else {
      setPromptState('selector');
    }
  }, [practitioners, severity]);

  // Handle practitioner selection
  const handleSelectPractitioner = useCallback(
    (practitioner: PractitionerCrisisConsent) => {
      setSelectedPractitioner(practitioner);
    },
    []
  );

  // Handle send request with duplicate prevention guard (P1-6)
  const handleSendRequest = useCallback(async () => {
    // P1-6: Prevent duplicate submissions during rapid taps
    if (isRequestInFlightRef.current) {
      return;
    }

    if (!selectedPractitioner || !moderationEventId) {
      return;
    }

    // Set guard before state update to prevent race condition
    isRequestInFlightRef.current = true;
    setPromptState('sending');
    setErrorMessage('');

    try {
      const result = await sendRequest({
        moderation_event_id: moderationEventId,
        practitioner_id: selectedPractitioner.practitioner_id,
        share_original_message: false, // Privacy-first default
        urgency_level: severityToUrgency(severity),
      });

      captureEvent(SAFETY_PRACTITIONER_EVENTS.REQUEST_SENT, {
        severity,
        urgency_level: severityToUrgency(severity),
        practitioner_notified: result.practitioner_notified,
        status: result.status,
      });

      setPromptState('success');
      onRequestSent?.(selectedPractitioner.practitioner_id);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'An error occurred';
      setErrorMessage(message);
      setPromptState('error');

      captureEvent(SAFETY_PRACTITIONER_EVENTS.REQUEST_FAILED, {
        severity,
        error: message,
      });
    } finally {
      // Reset guard after completion (success or error)
      isRequestInFlightRef.current = false;
    }
  }, [selectedPractitioner, moderationEventId, severity, sendRequest, onRequestSent]);

  // Handle retry
  const handleRetry = useCallback(() => {
    setPromptState('selector');
    setErrorMessage('');
  }, []);

  // Handle close and reset state
  const handleClose = useCallback(() => {
    setPromptState('prompt');
    setSelectedPractitioner(null);
    setErrorMessage('');
    onClose();
  }, [onClose]);

  // Don't render if no practitioners available
  const hasPractitioners = practitioners && practitioners.length > 0;

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={false}
          >
            {/* PROMPT STATE - Initial prompt */}
            {promptState === 'prompt' && (
              <PromptContent
                t={t}
                hasPractitioners={hasPractitioners}
                loadingPractitioners={loadingPractitioners}
                onRequestSupport={handleRequestSupport}
                onClose={handleClose}
              />
            )}

            {/* SELECTOR STATE - Practitioner selection */}
            {promptState === 'selector' && (
              <SelectorContent
                t={t}
                practitioners={practitioners || []}
                selectedPractitioner={selectedPractitioner}
                onSelect={handleSelectPractitioner}
                onSend={handleSendRequest}
                onCancel={handleClose}
              />
            )}

            {/* SENDING STATE - Loading */}
            {promptState === 'sending' && <SendingContent t={t} />}

            {/* SUCCESS STATE - Confirmation */}
            {promptState === 'success' && (
              <SuccessContent t={t} onDone={handleClose} />
            )}

            {/* ERROR STATE - Error with retry */}
            {promptState === 'error' && (
              <ErrorContent
                t={t}
                errorMessage={errorMessage}
                onRetry={handleRetry}
                onClose={handleClose}
              />
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );
}



export default CrisisPractitionerPrompt;